import { useEffect, useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  DateField,
  Edit,
  Create,
  SimpleForm,
  TextInput,
  DateInput,
  required,
  useRecordContext,
  useNotify,
  useRefresh,
  FunctionField,
} from "react-admin";
import { Box, Button, Chip, Paper, Stack, TextField as MuiTextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ActivateCell = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record) return null;

  if (record.is_current) {
    return (
      <Chip
        size="small"
        color="success"
        label="Corrente"
        icon={<CheckCircleIcon />}
        sx={{ fontWeight: 700 }}
      />
    );
  }

  const activate = async (e) => {
    e.stopPropagation();
    const token = localStorage.getItem("basicAuth");
    const res = await fetch(`/api/admin/seasons/${record.id}/activate`, {
      method: "PUT",
      headers: { Authorization: `Basic ${token}` },
    });
    if (!res.ok) {
      notify("Errore nell'attivazione della stagione", { type: "error" });
      return;
    }
    notify("Stagione attivata", { type: "success" });
    refresh();
  };

  return (
    <Button size="small" variant="contained" disableElevation onClick={activate}>
      Attiva
    </Button>
  );
};

export const SeasonList = () => (
  <List
    perPage={50}
    sort={{ field: "label", order: "DESC" }}
    sx={{ "& .RaList-content": { marginTop: 1 } }}
  >
    <Datagrid
      rowClick="edit"
      bulkActionButtons={false}
      rowSx={(record) => ({
        bgcolor: record.is_current ? "action.selected" : undefined,
      })}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        "& .RaDatagrid-headerCell": { fontWeight: 800 },
        "& .RaDatagrid-rowCell": { py: 1.5 },
      }}
    >
      <TextField source="label" label="Stagione" />
      <DateField source="start_date" label="Inizio" emptyText="—" />
      <DateField source="end_date" label="Fine" emptyText="—" />
      <FunctionField label="Stato" render={() => <ActivateCell />} />
    </Datagrid>
  </List>
);

const SeasonEditForm = () => (
  <SimpleForm>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, width: "100%" }}>
      <TextInput source="label" label="Stagione" disabled fullWidth />
      <Box />
      <DateInput source="start_date" label="Data inizio" fullWidth />
      <DateInput source="end_date" label="Data fine" fullWidth />
    </Box>
  </SimpleForm>
);

const SeasonCreateForm = () => (
  <SimpleForm>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, width: "100%" }}>
      <TextInput
        source="label"
        label="Stagione (es. 2026-2027)"
        validate={[required()]}
        fullWidth
      />
      <Box />
      <DateInput source="start_date" label="Data inizio" fullWidth />
      <DateInput source="end_date" label="Data fine" fullWidth />
    </Box>
  </SimpleForm>
);

const StandingsSourceSection = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [values, setValues] = useState({ op: "", tid: "", round: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record) return;
    const token = localStorage.getItem("basicAuth");
    setLoading(true);
    fetch(`/api/admin/standings-source?season_id=${record.id}`, {
      headers: { Authorization: `Basic ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setValues({ op: data.op, tid: data.tid, round: data.round });
      })
      .finally(() => setLoading(false));
  }, [record]);

  if (!record) return null;

  const handleChange = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
  };

  const save = async () => {
    const token = localStorage.getItem("basicAuth");
    const res = await fetch(`/api/admin/standings-source?season_id=${record.id}`, {
      method: "PUT",
      headers: {
        Authorization: `Basic ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        op: Number(values.op),
        tid: Number(values.tid),
        round: Number(values.round),
      }),
    });
    if (!res.ok) {
      notify("Errore nel salvataggio della configurazione classifica", { type: "error" });
      return;
    }
    notify("Configurazione classifica salvata", { type: "success" });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Classifica (sportintour.it)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Parametri del girone su sportintour.it da cui viene scaricata la classifica per questa stagione.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <MuiTextField
          label="op"
          type="number"
          value={values.op}
          onChange={handleChange("op")}
          disabled={loading}
          size="small"
        />
        <MuiTextField
          label="tid"
          type="number"
          value={values.tid}
          onChange={handleChange("tid")}
          disabled={loading}
          size="small"
        />
        <MuiTextField
          label="round"
          type="number"
          value={values.round}
          onChange={handleChange("round")}
          disabled={loading}
          size="small"
        />
        <Button variant="contained" disableElevation onClick={save} disabled={loading}>
          Salva
        </Button>
      </Stack>
    </Paper>
  );
};

export const SeasonEdit = () => (
  <Edit title="Modifica stagione">
    <>
      <SeasonEditForm />
      <StandingsSourceSection />
    </>
  </Edit>
);

export const SeasonCreate = () => (
  <Create title="Nuova stagione">
    <SeasonCreateForm />
  </Create>
);
